import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import type { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityComboboxMulti } from "@/components/ui/entity-combobox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { type Skill, useCreateSkill, useUpdateSkill } from "@/hooks/use-skills";
import { type SkillCreateInput, skillCreateSchema, skillStatusValues } from "@/schemas/skills";

export function SkillFormDialog({
  open,
  onOpenChange,
  skill,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skill?: Skill;
}) {
  const isEdit = skill != null;
  const create = useCreateSkill();
  const update = useUpdateSkill(skill?.id ?? "");
  // Three generics, not one: the schema has `.default()` fields, so its INPUT
  // type has them optional while its OUTPUT type has them required. Passing
  // only the output type makes the resolver's own input type unassignable.
  const form = useForm<z.input<typeof skillCreateSchema>, unknown, SkillCreateInput>({
    resolver: zodResolver(skillCreateSchema),
    defaultValues: {
      name: skill?.name ?? "",
      description: skill?.description ?? "",
      categoryIds: skill?.categories.map((c) => c.id) ?? [],
      status: skill?.status ?? "draft",
    },
  });

  // Route every close (cancel/escape/backdrop) through a reset so reopening
  // never shows a previous session's abandoned input.
  function handleOpenChange(next: boolean) {
    if (!next) form.reset();
    onOpenChange(next);
  }

  async function onSubmit(values: SkillCreateInput) {
    const payload = { ...values, description: values.description?.trim() || null };
    if (isEdit) await update.mutateAsync(payload);
    else await create.mutateAsync(payload);
    form.reset();
    onOpenChange(false);
  }

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit skill" : "Add skill"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Update this skill's details and status." : "Add a new skill to the library."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. TypeScript" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value ?? ""}
                      rows={3}
                      placeholder="Optional summary"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="categoryIds"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Categories</FormLabel>
                  <EntityComboboxMulti
                    entity="categories"
                    value={field.value ?? []}
                    onChange={field.onChange}
                    placeholder="None"
                    searchPlaceholder="Search categories…"
                    aria-invalid={Boolean(fieldState.error)}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {skillStatusValues.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? "Saving…" : isEdit ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
